import { CRField } from '@smart-builder/types';
import { isCRField } from '@smart-builder/util';
import { createMainResourceREST } from './main';
import { actionTypeForFailure, actionTypeForSuccess } from './util';

// There's a bug in babel that prevents us from
//    only export specific types from below.
// Hence, exporting all types.
// See https://github.com/babel/babel/issues/8361
export * from './main/types';

export {
  actionTypeForSuccess as resourceActionTypeForSuccess,
  actionTypeForFailure as resourceActionTypeForFailure,
  createMainResourceREST,
};

const fieldValueToSummaryString = (field?: CRField) => {
  if (field) {
    return field.client.value;
  } else {
    return undefined;
  }
};

// this is defined in a curried manner so `summary` and `body` can be partially applied firstly for convenicene
// secondly, the `K` type parameter needs to be nested so it can be instantiated to a new type on each (curried)
// e.g.
// ```
// const doMerge = mergeSummaryBodyField<SomeConcreteSummary, SomeConcreteBody>(summary, body);
// ```
// each use of doMerge should be allowed to have a new value for `K` otherwise `K` will be inferred as having a type
// of the union of all uses rather than being specific to each use which is definitely not what we want
export const mergeSummaryBodyField =
  <TItemSummary, TItemBody>(
    summary: TItemSummary | undefined,
    body: TItemBody | undefined,
  ) =>
  <K extends keyof TItemSummary & keyof TItemBody>(
    fieldName: K,
  ): TItemSummary[K] => {
    const bodyValue: TItemBody[K] | undefined = body?.[fieldName];
    const unwrappedBodyValue = isCRField(bodyValue)
      ? fieldValueToSummaryString(bodyValue)
      : bodyValue;

    const summaryValue = summary?.[fieldName];
    // NOTE!
    // the body field (once unwrapped) should have the same type as the summary
    // but not sure of a way to enforce this though the type system
    // we want a constraint something like `where UnwrappedCRField<TItemBody[K]> == TItemSummary[K]>`
    const val: TItemSummary[K] | undefined =
      unwrappedBodyValue === undefined
        ? undefined
        : (unwrappedBodyValue as unknown as TItemSummary[K]) ?? summaryValue;

    // TODO to match old behaviour we are asserting the end value is nonnull even though this isn't necessarily true
    return val!;
  };
